// theme.ts
import type { Theme } from '@aws-amplify/ui-react';

export const sumaqtTheme: Theme = {
  name: 'sumaqt',
  tokens: {
    fonts: {
      default: {
        variable: {
          value:
            'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
        },
      },
    },

    radii: {
      small: { value: '10px' },
      medium: { value: '14px' },
      large: { value: '18px' },
      xl: { value: '24px' },
    },

    space: {
      xs: { value: '0.5rem' },
      small: { value: '0.75rem' },
      medium: { value: '1rem' },
      large: { value: '1.25rem' },
      xl: { value: '1.75rem' },
      xxl: { value: '2.25rem' },
    },

    colors: {
      brand: {
        primary: {
          10: { value: '#eff6ff' },
          20: { value: '#dbeafe' },
          80: { value: '#2563eb' },
          90: { value: '#1e40af' },
          100: { value: '#172554' },
        },
      },

      font: {
        primary: { value: '#e5e7eb' },
        secondary: { value: '#cbd5e1' },
        tertiary: { value: 'rgba(226,232,240,0.72)' },
        inverse: { value: '#0b1020' },
      },

      background: {
        primary: { value: 'rgba(255,255,255,0.07)' },   // glass main
        secondary: { value: 'rgba(255,255,255,0.05)' }, // inputs
        tertiary: { value: 'rgba(255,255,255,0.035)' },
      },

      border: {
        primary: { value: 'rgba(255,255,255,0.14)' },
        secondary: { value: 'rgba(255,255,255,0.10)' },
        tertiary: { value: 'rgba(255,255,255,0.08)' },
      },
    },

    shadows: {
      small: { value: '0 10px 28px rgba(0,0,0,0.25)' },
      medium: { value: '0 18px 60px rgba(0,0,0,0.35)' },
      large: { value: '0 26px 90px rgba(0,0,0,0.45)' },
    },

    components: {
      authenticator: {
        container: {
          maxWidth: { value: '460px' },
          padding: { value: '{space.xxl}' },
          borderRadius: { value: '{radii.xl}' },
          backgroundColor: { value: '{colors.background.primary}' },
          borderWidth: { value: '1px' },
          borderStyle: { value: 'solid' },
          borderColor: { value: '{colors.border.primary}' },
          boxShadow: { value: '{shadows.large}' },
        },
      },

      heading: {
        1: {
          fontSize: { value: '1.35rem' },
          fontWeight: { value: '800' },
          letterSpacing: { value: '-0.02em' },
          color: { value: '{colors.font.primary}' },
        },
      },

      text: {
        primary: { color: { value: '{colors.font.primary}' } },
        secondary: { color: { value: '{colors.font.secondary}' } },
        tertiary: { color: { value: '{colors.font.tertiary}' } },
      },

      tabs: {
        list: {
          gap: { value: '{space.small}' },
          padding: { value: '{space.xs}' },
          backgroundColor: { value: '{colors.background.tertiary}' },
          borderRadius: { value: '{radii.large}' },
          borderWidth: { value: '1px' },
          borderStyle: { value: 'solid' },
          borderColor: { value: '{colors.border.tertiary}' },
        },
        item: {
          paddingInline: { value: '{space.large}' },
          paddingBlock: { value: '{space.small}' },
          borderRadius: { value: '{radii.large}' },
          color: { value: '{colors.font.secondary}' },
          _hover: {
            color: { value: '{colors.font.primary}' },
            backgroundColor: { value: '{colors.background.tertiary}' },
          },
          _active: {
            color: { value: '{colors.font.primary}' },
          },
        },
      },

      fieldcontrol: {
        borderRadius: { value: '{radii.large}' },
        backgroundColor: { value: '{colors.background.secondary}' },
        borderWidth: { value: '1px' },
        borderStyle: { value: 'solid' },
        borderColor: { value: '{colors.border.secondary}' },
        color: { value: '{colors.font.primary}' },
        paddingInline: { value: '{space.medium}' },
        paddingBlock: { value: '{space.small}' },
        _focus: {
          borderColor: { value: '{colors.brand.primary.80}' },
          boxShadow: { value: '0 0 0 4px rgba(37,99,235,0.25)' },
        },
      },

      button: {
        primary: {
          borderRadius: { value: '{radii.large}' },
          fontWeight: { value: '800' },
          letterSpacing: { value: '-0.01em' },
          paddingBlock: { value: '{space.small}' },
          paddingInline: { value: '{space.large}' },
          backgroundColor: { value: '{colors.brand.primary.80}' },
          color: { value: '#ffffff' },
          _hover: {
            backgroundColor: { value: '{colors.brand.primary.90}' },
            transform: { value: 'translateY(-1px)' },
          },
          _active: {
            transform: { value: 'translateY(0px)' },
          },
        },

        link: {
          color: { value: '{colors.font.secondary}' },
          _hover: { color: { value: '{colors.font.primary}' } },
        },
      },

      divider: {
        borderColor: { value: '{colors.border.tertiary}' },
      },
    },
  },
};
